import { defineAsyncComponent, defineComponent, onMounted, ref, watch } from 'vue'
import { BlockModule } from '../common'
import { useNavContext } from '../main-navigation/composables'
import { scrollToTopOfPage } from '@/lib/scrolling'
import { useScriptTag, useTimeoutFn } from '@vueuse/core'
import ChatbotLauncherIcon from './chatbot-launcher-icon.vue'
import ChatbotIntroBubble from './chatbot-intro-bubble.vue'
import { INTRO_BUBBLE_DELAY, INTRO_BUBBLE_MESSAGE, INTRO_DISMISSED_STORAGE_KEY } from './constants'

// Guarded: browsers blocking storage throw here, and the intro just shows again.
const readIntroDismissed = () => {
    try {
        return sessionStorage.getItem(INTRO_DISMISSED_STORAGE_KEY) === 'true'
    } catch {
        return false
    }
}

const persistIntroDismissed = () => {
    try {
        sessionStorage.setItem(INTRO_DISMISSED_STORAGE_KEY, 'true')
    } catch {
        return
    }
}

new BlockModule({
    selector: '.block-scroll-to-top-and-chatbot',
    vueComponent: defineComponent({
        name: 'block-scroll-to-top-and-chatbot',
        components: {
            FadeInOutGroup: defineAsyncComponent(
                () => import('@/components/website/transition/FadeInOutGroup.vue')
            ),
            MsigButton: defineAsyncComponent(
                () => import('@/components/website/button/button.vue')
            ),
            ChatbotLauncherIcon,
            ChatbotIntroBubble,
        },
        props: {
            widgetUrl: String
        },
        setup(props) {
            const { isNavHidden } = useNavContext()

            const isBusy = ref(false)
            const isOriChatbotButtonHidden = ref(false)

            // Launcher intro: float+blink animation and the speech bubble above the icon.
            const isIntroAnimating = ref(false)
            const isBubbleVisible = ref(false)
            let isIntroDismissed = readIntroDismissed()

            const { start: startBubbleDelay, stop: stopBubbleDelay } = useTimeoutFn(
                () => { isBubbleVisible.value = true },
                INTRO_BUBBLE_DELAY,
                { immediate: false },
            )

            const startIntro = () => {
                if (isIntroDismissed) return
                isIntroAnimating.value = true
                startBubbleDelay()
            }

            // Ends the intro for the rest of the session — on bubble close or when the chat is opened.
            const dismissIntro = () => {
                if (isIntroDismissed) return
                isIntroDismissed = true
                persistIntroDismissed()
                stopBubbleDelay()
                isIntroAnimating.value = false
                isBubbleVisible.value = false
            }

            const moveChatbot = () => {
                const chatbotWidgetEl = document.getElementById('msig-chatbot-widget')
                if (!chatbotWidgetEl) return

                chatbotWidgetEl.classList.add('transition-all')

                if (isNavHidden.value) {
                    chatbotWidgetEl.classList.add('-translate-y-12', 'md:-translate-y-16', 'lg:-translate-y-24')
                } else {
                    chatbotWidgetEl.classList.remove('-translate-y-12', 'md:-translate-y-16', 'lg:-translate-y-24')
                }
            }

            const { load } = useScriptTag(props.widgetUrl || "",
                () => {
                    window.CHATBOT_WIDGET_API.on('ready', (iframe: HTMLIFrameElement | null) => {
                        moveChatbot()
                        isBusy.value = false
                        isOriChatbotButtonHidden.value = true
                        // if (!iframe) return
                        // iframe.style.display = ''

                        window.CHATBOT_WIDGET_API.maximize()
                    })
                }, 
                {
                    manual: true,
                    attrs: {
                        'data-script': 'msig-chatbot',
                        'data-loading-method': 'manual',
                    }
                })

            const loadChatbot = () => {
                dismissIntro()
                try {
                    isBusy.value = true
                    load()
                } catch(err) {
                    isBusy.value = false
                }
            }

            watch(isNavHidden, (val) => {
                moveChatbot()
            })

            onMounted(() => {
                startIntro()
            })

            return {
                isNavHidden,
                isOriChatbotButtonHidden,
                isBusy,
                scrollToTopOfPage,
                loadChatbot,
                isIntroAnimating,
                isBubbleVisible,
                introMessage: INTRO_BUBBLE_MESSAGE,
                onIntroBubbleClose: dismissIntro,
            }
        },
    }),
})
